import {
  createElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode
} from 'react'
import { createPortal } from 'react-dom'

import { cn } from '../../utils/cn'

type TooltipProps = {
  /** The tooltip body. When empty, the trigger renders plain (no cursor, no bubble). */
  content?: ReactNode
  children: ReactNode
  /** Classes applied to the trigger wrapper. */
  className?: string
  /** Element rendered as the hover target. Use 'div' to preserve block/flex layout. */
  as?: 'span' | 'div'
  /** Preferred side; flips to the opposite edge if there isn't room. */
  side?: 'top' | 'bottom'
}

/**
 * Lightweight hover/focus tooltip rendered through a portal so it can't be
 * clipped by `overflow-hidden` cards. Replaces the native `title` attribute,
 * which is slow, unstyled, and frequently fails to appear.
 */
export function Tooltip({ content, children, className, as = 'span', side = 'top' }: TooltipProps) {
  const triggerRef = useRef<HTMLElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const tip = tooltipRef.current
    const tipHeight = tip?.offsetHeight ?? 32
    const tipWidth = tip?.offsetWidth ?? 200
    const margin = 8
    let top = side === 'top' ? rect.top - tipHeight - margin : rect.bottom + margin
    // Flip below the trigger when there isn't room above.
    if (side === 'top' && top < margin) top = rect.bottom + margin
    let left = rect.left + rect.width / 2 - tipWidth / 2
    left = Math.max(margin, Math.min(left, window.innerWidth - tipWidth - margin))
    setPos({ top, left })
  }, [side])

  // Position before paint so the bubble never flashes at the origin.
  useLayoutEffect(() => {
    if (open) updatePosition()
  }, [open, updatePosition])

  // Keep it anchored while scrolling/resizing during a hover.
  useEffect(() => {
    if (!open) return
    const handler = () => updatePosition()
    window.addEventListener('scroll', handler, true)
    window.addEventListener('resize', handler)
    return () => {
      window.removeEventListener('scroll', handler, true)
      window.removeEventListener('resize', handler)
    }
  }, [open, updatePosition])

  if (!content) {
    return createElement(as, { className }, children)
  }

  return (
    <>
      {createElement(
        as,
        {
          ref: triggerRef,
          className: cn('cursor-help', className),
          onMouseEnter: () => setOpen(true),
          onMouseLeave: () => setOpen(false),
          onFocus: () => setOpen(true),
          onBlur: () => setOpen(false)
        },
        children
      )}
      {open &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            style={{ position: 'fixed', top: pos.top, left: pos.left }}
            className="pointer-events-none z-[100] max-w-xs rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs font-medium leading-snug text-popover-foreground shadow-md"
          >
            {content}
          </div>,
          document.body
        )}
    </>
  )
}
